/**
 * @file        src/components/catalog/CarCardActions.tsx
 * @description Client wrapper for the "Оставить заявку" CTA on a CarCard.
 *              Holds the modal open state; renders LeadFormModal, which owns
 *              the dialog itself (focus trap, focus return, Escape, background
 *              scroll lock).
 *              Extracted so CarCard itself can be a server component (SSR for
 *              headless agents).
 * @universal   knowledge/universals/components.md#carcardactions
 */
"use client";

import { useState } from "react";
import LeadFormModal from "@/components/LeadFormModal";
import { carDisplayName } from "@/lib/carUtils";
import type { Car } from "@/types/car";

interface CarCardActionsProps {
  car: Car;
}

export default function CarCardActions({ car }: CarCardActionsProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // One string, two consumers. The heading the visitor reads and the subject
  // the manager reads in Telegram must name the same car — computing it once
  // is what makes drifting apart impossible rather than merely unlikely.
  const carTitle = carDisplayName(car);

  return (
    <>
      <div className="px-4 pb-4">
        <button
          onClick={() => setIsModalOpen(true)}
          className="mt-2 w-full cursor-pointer rounded-xl border border-primary px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white"
        >
          Оставить заявку
        </button>
      </div>

      {/* @rule source="catalog-car-card" is the key for the lead_catalog_car
          goal in LEAD_GOAL_BY_SOURCE. Changing or dropping it silently zeroes
          that goal in Metrika — no build error, no lint error, no runtime
          error, just a report that reads 0 forever. */}
      <LeadFormModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        title={carTitle}
        subject={carTitle}
        source="catalog-car-card"
        ctaLabel="Отправить заявку"
      />
    </>
  );
}
